// What every Agents screen shares after the 2026-10-21 UX pass (docs/design/2026-10-21-agents-ux-pass.md):
// - the add-on-off banner: the Agents mode stays in navigation; while the `agents` add-on is off
//   a screen shows one plain line saying so and who can turn it on, and only real data under it
//   (founder, 2026-10-26: no sample or example rows in any deployed screen);
// - hover actions: the row's icon buttons (open, message, pause, keys…), revealed on hover or
//   focus and always on coarse pointers (`ListRow actions`), each named for readers and tooltipped;
// - an agent's status word (live / idle / paused / setup / retired) from its lifecycle and latest
//   run, as a dot badge;
// - "Message": the DM with an agent (`dms.open`, idempotent on the member set), then Chat;
// - the Agents document frame: Agents renders edge to edge (a location header over a list), so a
//   settings-shaped screen wraps itself in `AgentsDoc`.
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Notice,
  PageFrame,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { BlocksIcon, MessageSquareIcon } from "lucide-react";
import type { ComponentProps, ComponentType } from "react";
import type { Api } from "../../api/client";
import type { DeSummary, RunRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { useSession } from "../use-feature";
import { isLive } from "./agent-parts";
import { reachRefusalToast } from "./ask-access";

/** An agent's one status word (research rec. 11: idle, running, awaiting approval, parked). */
export type AgentStatus = "live" | "idle" | "paused" | "setup" | "retired" | "not_deployed";

export const AGENT_STATUS_LABELS: Record<AgentStatus, string> = {
  live: "Live",
  idle: "Idle",
  paused: "Paused",
  setup: "Setup",
  retired: "Retired",
  not_deployed: "Not deployed",
};

const STATUS_TONE: Record<AgentStatus, "success" | "neutral" | "warning" | "info"> = {
  live: "success",
  idle: "neutral",
  paused: "warning",
  setup: "info",
  retired: "neutral",
  not_deployed: "neutral",
};

/** Lifecycle first (a paused DE is paused whatever its last run), then the latest run. */
export function agentStatus(status: DeSummary["status"], latest: RunRow | undefined): AgentStatus {
  if (status === "paused") return "paused";
  if (status === "retired") return "retired";
  if (status === "not_deployed") return "not_deployed";
  if (status === "onboarding") return "setup";
  return latest && isLive(latest.state) ? "live" : "idle";
}

export function AgentStatusBadge({ status }: { status: AgentStatus }) {
  return (
    <Badge variant={STATUS_TONE[status]} dot data-agent-status={status}>
      {AGENT_STATUS_LABELS[status]}
    </Badge>
  );
}

/**
 * The one line of a screen while the `agents` add-on is off: that it is off and who turns it on.
 * Add-ons are switched by the CompanyOS operator only (Setup › Add-ons and
 * `operator-entitlements` both require `company#agency_operator`), so the operator gets the
 * Setup › Add-ons link, an owner or admin is told to ask the operator, and anyone else to ask an
 * owner or admin. Never sample rows.
 */
export function AgentsOffBanner({
  company,
  className = "mx-6 mt-4 max-md:mx-4",
}: {
  company: CompanyAccess;
  /** The edge-to-edge screens inset it; a document page (already padded) passes "". */
  className?: string;
}) {
  const operator = company.roles.includes("agency_operator");
  const ask = operator
    ? ""
    : isManager(company)
      ? " Ask your CompanyOS operator to turn it on."
      : ` Ask an owner or admin of ${company.name} to have it turned on.`;
  return (
    <Notice
      tone="info"
      icon={BlocksIcon}
      className={className}
      data-slot="agents-off"
      action={
        operator ? (
          <Link
            to="/c/$slug/setup/addons"
            params={{ slug: company.slug }}
            search={keepFocus}
            className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
          >
            Turn on in Setup › Add-ons
          </Link>
        ) : null
      }
    >
      {`Agents aren't turned on for this company.${ask}`}
    </Notice>
  );
}

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

/**
 * One hover action of a row: a 28 px ghost icon button, named for readers (`label`) and
 * tooltipped for everyone.
 */
export function RowAction({
  icon: Icon,
  label,
  hint,
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "children" | "render"> & {
  icon: IconType;
  label: string;
  /** WF-FIX: what the action really does, under the label in the tooltip and as a description. */
  hint?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            {...(hint ? { "aria-description": hint } : {})}
            className={className}
            {...props}
          />
        }
      >
        <Icon aria-hidden={true} />
      </TooltipTrigger>
      <TooltipContent>
        {hint ? (
          <span className="flex max-w-64 flex-col gap-0.5">
            <span>{label}</span>
            <span className="text-2xs opacity-80">{hint}</span>
          </span>
        ) : (
          label
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** A hover action that is a router link (a tab of the profile, a Setup page, Chat). */
export function RowLinkAction({
  icon: Icon,
  label,
  to,
  params,
  search = keepFocus,
  className,
}: {
  icon: IconType;
  label: string;
  to: string;
  params?: Record<string, string>;
  search?: unknown;
  className?: string;
}) {
  // The route is one of the app's; the generic Link props are too wide to name here.
  const link = { to, params, search } as unknown as ComponentProps<typeof Link>;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Link
            {...link}
            aria-label={label}
            className={cn(buttonVariants({ variant: "ghost", size: "icon-sm" }), className)}
          />
        }
      >
        <Icon aria-hidden={true} />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** The caller's own (person) membership in the company, from `/v1/me`; null for an agency grant. */
export function useMyMembershipId(companyId: string): string | null {
  const { me } = useSession();
  return (
    me.memberships.find((m) => m.company_id === companyId && m.kind === "person")?.membership_id ??
    null
  );
}

/** The DM with a member: opened (or found) and then shown in Chat. */
export function useOpenDm(api: Api, company: CompanyAccess) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (membershipId: string) =>
      api.client.dms.open({ params: { co: company.id }, body: { membership_ids: [membershipId] } }),
    onSuccess: (channel) =>
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: company.slug, channelId: channel.id },
        search: keepFocus,
      } as never),
    // REACH-S1: outside the agent's Hears from — the toast offers "Ask for access".
    onError: (e) => void reachRefusalToast(api, company.id, e),
  });
}

/** "Message": opens the DM with the agent (one hover action, the same everywhere). */
export function MessageAction({
  api,
  company,
  membershipId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  membershipId: string;
  name: string;
}) {
  const dm = useOpenDm(api, company);
  return (
    <RowAction
      icon={MessageSquareIcon}
      label={`Message ${name}`}
      disabled={dm.isPending}
      onClick={() => dm.mutate(membershipId)}
    />
  );
}

/** The document frame of a settings-shaped Agents screen (Agents' main is edge to edge). */
export function AgentsDoc({ className, ...props }: ComponentProps<"div">) {
  return <PageFrame width="doc" className={cn("min-h-full", className)} {...props} />;
}

/** A list body of Agents: a location header, then rows edge to edge. */
export const LIST_BODY = "flex min-w-0 flex-1 flex-col";
